import { useMemo, useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import TvCard from '@/components/base/TvCard';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { tvSeriesList, tvRegions, tvGenres, tvYears, tvSortOptions } from '@/mocks/tvSeries';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 8;

export default function TvSeriesCategory() {
  usePageMeta(
    '漂亮的姐姐 - 电视剧分类 - 热门影视推荐',
    '漂亮的姐姐电视剧分类页，汇集古装、现代、悬疑、爱情等多种类型电视剧，支持按地区、年份筛选与热度排序，快速找到想看的热门影视作品。',
  );

  const [genre, setGenre] = useState('全部类型');
  const [region, setRegion] = useState('全部地区');
  const [year, setYear] = useState('全部年份');
  const [sort, setSort] = useState('默认排序');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();

  const filtered = useMemo(() => {
    let list = tvSeriesList.filter((item) => {
      const matchGenre = genre === '全部类型' || item.tags.includes(genre);
      const matchRegion = region === '全部地区' || item.region === region;
      const matchYear = year === '全部年份' || String(item.year) === year;
      return matchGenre && matchRegion && matchYear;
    });
    if (sort === '热度优先') list = [...list].sort((a, b) => b.popularity - a.popularity);
    if (sort === '最新上架') list = [...list].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    if (sort === '评分最高') list = [...list].sort((a, b) => b.rating - a.rating);
    return list;
  }, [genre, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetFilters = () => {
    setGenre('全部类型');
    setRegion('全部地区');
    setYear('全部年份');
    setSort('默认排序');
    setPage(1);
  };

  const updateFilter = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const selectClass =
    'h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-400 transition-colors cursor-pointer';

  const filterFields = (
    <>
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-label text-foreground-500">类型</span>
        <select className={selectClass} value={genre} onChange={(e) => updateFilter(setGenre)(e.target.value)} aria-label="按类型筛选">
          {tvGenres.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-label text-foreground-500">地区</span>
        <select className={selectClass} value={region} onChange={(e) => updateFilter(setRegion)(e.target.value)} aria-label="按地区筛选">
          {tvRegions.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-label text-foreground-500">年份</span>
        <select className={selectClass} value={year} onChange={(e) => updateFilter(setYear)(e.target.value)} aria-label="按年份筛选">
          {tvYears.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-label text-foreground-500">排序</span>
        <select className={selectClass} value={sort} onChange={(e) => updateFilter(setSort)(e.target.value)} aria-label="排序方式">
          {tvSortOptions.map((item) => (
            <option key={item} value={item}>{item}</option>
          ))}
        </select>
      </label>
    </>
  );

  return (
    <div className="w-full max-w-page mx-auto px-4 md:px-6 py-6">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '电视剧分类' }]} />

      <header className="mt-5">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">电视剧分类</h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
          汇集古装、现代、悬疑、爱情、都市、年代等多种类型电视剧，共收录 {tvSeriesList.length} 部优质作品。
          支持按类型、地区、年份筛选，并按热度、上架时间或评分排序，帮你快速锁定想看的剧。
        </p>
      </header>

      <div className="mt-6 bg-background-50 border border-background-200 rounded-lg p-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-label text-foreground-700 flex items-center gap-2">
            <i className="ri-filter-3-line text-primary-600"></i>
            筛选条件
          </span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={resetFilters} className="text-xs font-label text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap">
              重置筛选
            </button>
            <button
              type="button"
              onClick={() => setFilterOpen((prev) => !prev)}
              className="md:hidden inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-background-300 text-xs font-label text-foreground-700 cursor-pointer whitespace-nowrap"
            >
              {filterOpen ? '收起' : '展开'}
              <i className={filterOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
            </button>
          </div>
        </div>
        <div className={`${filterOpen ? 'grid' : 'hidden'} md:grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mt-4`}>
          {filterFields}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-between">
        <p className="text-sm text-foreground-600">
          共找到 <strong className="text-primary-700">{filtered.length}</strong> 部电视剧
        </p>
        <p className="text-sm text-foreground-500 hidden sm:block">第 {safePage} / {totalPages} 页</p>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {pageItems.map((series) => (
              <TvCard key={series.id} series={series} favorite={isFavorite(series.id)} onToggleFavorite={toggleFavorite} />
            ))}
          </div>
          <Pagination current={safePage} total={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-4">
          <EmptyState
            title="暂无符合条件的电视剧"
            description="试试减少筛选条件，或点击下方按钮重置全部筛选，查看更多热门剧集。"
            actionLabel="重置筛选条件"
            onAction={resetFilters}
          />
        </div>
      )}
    </div>
  );
}